import {
  AutoComplete,
  Button,
  Cascader,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Radio,
  Upload,
  Modal,
} from "antd";
import React, { useState } from "react";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { WidgetType } from "./index";
import RichTextEditor from "./RichTextEditor";
import SupplierSelectInput from "@/pages/supplier/SupplierProfile/components/AccessedTab/components/PriceApplicationModal/components/SupplierSelectInput";
import styles from "./index.module.less";

const ErrorWidget = ({ message }: any) => {
  message =
    message ||
    `你的组件配置有问题，请确认组件类型配置是否正确。现在包含的组件有
    antd: Input、Select、DatePicker、InputNumber、Checkbox、Button、Upload一些基础组件属性上面的组件用点分割，如Input.TextArea`;
  return React.createElement("span", null, message);
};
/**
 * Input、Input.Search、Input.TextArea
 *
 */
const WidgetInput = React.memo((props: any) => {
  // Input 组件不支持 loading 属性，需要过滤掉
  let { widgetType, loading, disabled, value, ...widget } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  const displayValue = disabled && !value ? "-" : value;

  if (widgetType === "Input") {
    return <Input {...widget} disabled={disabled} value={displayValue} />;
  } else if (widgetType.startsWith("Input.")) {
    const inputWidget = widgetType.slice(6);
    //@ts-ignore
    return React.createElement(Input[inputWidget], {
      ...widget,
      disabled,
      value: displayValue,
    });
  } else {
    return <ErrorWidget />;
  }
});
/**
 *  DatePicker DatePicker.RangePicker
 */
const WidgetDatePicker = React.memo((props: any) => {
  let { widgetType, disabled, value, ...widget } = props;

  // 在只读/禁用模式下，格式化日期显示
  let displayValue = value;
  if (disabled && !value) {
    displayValue = null;
  } else if (disabled && value) {
    if (Array.isArray(value)) {
      // RangePicker 返回数组 - 保持原值，让组件处理显示
      displayValue = value;
    } else {
      // DatePicker 返回单个日期 - 保持原值
      displayValue = value;
    }
  }

  if (widgetType === "DatePicker") {
    return (
      <DatePicker
        {...widget}
        disabled={disabled}
        value={displayValue}
        placeholder={disabled && !value ? "-" : undefined}
      />
    );
  } else if (widgetType.startsWith("DatePicker.")) {
    const dateWidget = widgetType.slice(11);
    //@ts-ignore
    return React.createElement(DatePicker[dateWidget], {
      ...widget,
      disabled,
      value: displayValue,
      placeholder: disabled && !value ? "-" : undefined,
    });
  } else {
    return <ErrorWidget />;
  }
});
/**
 *  Checkbox Checkbox.Group
 */
const WidgetCheckbox = React.memo((props: any) => {
  let { widgetType, label, disabled, value, options, ...widget } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  if (disabled && !value) {
    return <span>-</span>;
  }

  // 在只读/禁用模式下，显示对应的标签
  if (disabled && value && widgetType === "Checkbox.Group") {
    const selectedLabels = (Array.isArray(value) ? value : [value])
      .map((v: any) => {
        const opt = options?.find((o: any) => o.value === v);
        return opt?.label || v;
      })
      .join(", ");
    return <span>{selectedLabels}</span>;
  }

  if (widgetType === "Checkbox") {
    return (
      <Checkbox {...widget} disabled={disabled} checked={value}>
        {label}
      </Checkbox>
    );
  } else if (widgetType.startsWith("Checkbox.")) {
    const checkboxWidget = widgetType.slice(9);
    //@ts-ignore
    return React.createElement(Checkbox[checkboxWidget], {
      ...widget,
      disabled,
      value,
      options,
    });
  } else {
    return <ErrorWidget />;
  }
});
/**
 *  Radio Radio.Group
 */
const WidgetRadio = React.memo((props: any) => {
  let { widgetType, label, ...widget } = props;
  if (widgetType === "Radio") {
    return <Radio {...widget}>{label}</Radio>;
  } else if (widgetType.startsWith("Radio.")) {
    const radioWidget = widgetType.slice(6);
    //@ts-ignore
    return React.createElement(Radio[radioWidget], { ...widget });
  } else {
    return <ErrorWidget />;
  }
});
/**
 * Button,
 */
const WidgetButton = React.memo((props: any) => {
  const { text, ...buttonProps } = props;
  return <Button {...buttonProps}>{text}</Button>;
});
/**
 * Upload, Upload.Dragger
 * 支持照片墙模式和showUploadList配置
 */
const WidgetUpload = React.memo((props: any) => {
  let { widgetType, uploadButton, ...widget } = props;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // 根据 listType 提供不同的默认上传按钮
  const listType = widget?.listType || "picture-card";

  // 移除无效的 value 属性，Upload 组件应使用 fileList
  const {
    value,
    showUploadList,
    onPreview: customOnPreview,
    ...uploadProps
  } = widget;

  // 设置showUploadList配置，默认为true
  // 当showUploadList为对象时，可以配置removeIcon、downloadIcon等
  const uploadListConfig =
    showUploadList !== undefined
      ? showUploadList
      : {
          showPreviewIcon: true,
          showRemoveIcon: true,
          showDownloadIcon: false,
        };

  // 检查是否为图片URL
  const isImageUrl = (url: string) => {
    if (!url) return false;
    return (
      /\.(gif|jpg|jpeg|png|GIF|JPG|JPEG|PNG|webp|WEBP)$/.test(url) ||
      url.startsWith("data:image/")
    );
  };

  // 默认预览处理函数
  const handlePreview = async (file: any) => {
    // 如果用户提供了自定义的onPreview，则调用它
    if (customOnPreview && typeof customOnPreview === "function") {
      customOnPreview(file);
      return;
    }

    // 否则使用默认的预览处理
    let src = file.url || file.preview;

    if (!src) {
      // 如果是上传中或失败的文件，使用FileReader读取preview
      if (file.originFileObj) {
        src = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj);
          reader.onload = () => resolve(reader.result as string);
        });
      }
    }

    if (src) {
      setPreviewImage(src);
      setPreviewTitle(
        file.name ||
          file.url?.substring(file.url?.lastIndexOf("/") + 1) ||
          "预览",
      );
      setPreviewOpen(true);
    }
  };

  const getDefaultUploadButton = () => {
    if (listType === "text") {
      // 文本列表模式：简单的按钮
      return (
        <Button
          type="primary"
          icon={<UploadOutlined />}
          style={{
            width: 110,
            height: 32,
            background: "#FFFFFF",
            borderRadius: "4px",
            border: "1px solid #165DFF",
            color: "#165DFF",
          }}
        >
          上传文件
        </Button>
      );
    } else {
      // 卡片模式（picture-card）：图标 + 文字的竖排样式
      return (
        <button style={{ border: 0, background: "none" }} type="button">
          <PlusOutlined style={{ fontSize: 28, color: "#4E5969" }} />
          <div style={{ marginTop: 8, fontSize: 14, color: "#4E5969" }}>
            上传
          </div>
        </button>
      );
    }
  };

  if (widgetType === "Upload") {
    // 根据 listType 和 showUploadList 的类型，决定最终配置
    let finalShowUploadList = uploadListConfig;

    // 如果 listType 是 picture 或 picture-card，且 showUploadList 是布尔值，则转换为配置对象
    if (
      (listType === "picture" || listType === "picture-card") &&
      typeof uploadListConfig === "boolean" &&
      uploadListConfig
    ) {
      finalShowUploadList = {
        showPreviewIcon: true,
        showRemoveIcon: true,
        showDownloadIcon: false,
      };
    }

    const finalUploadProps = {
      ...uploadProps,
      listType,
      showUploadList: finalShowUploadList,
      onPreview: handlePreview,
      // 添加isImageUrl配合showPreviewIcon使用
      isImageUrl: isImageUrl,
    };

    return (
      <>
        <div className={styles.uploadWrapper}>
          <Upload {...finalUploadProps}>
            {uploadButton || getDefaultUploadButton()}
          </Upload>
        </div>
        {/* 预览模态框 */}
        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
          centered
          width={800}
        >
          {previewImage.endsWith(".pdf") ||
          previewImage.includes("application/pdf") ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>PDF 文件预览</p>
              <a href={previewImage} target="_blank" rel="noopener noreferrer">
                点击下载查看
              </a>
            </div>
          ) : (
            <img
              alt={previewTitle}
              style={{
                width: "100%",
                maxHeight: "600px",
                objectFit: "contain",
              }}
              src={previewImage}
            />
          )}
        </Modal>
      </>
    );
  } else if (widgetType === "Upload.Dragger") {
    // 处理 Dragger 的 showUploadList 配置
    let finalShowUploadListDragger = uploadListConfig;

    if (typeof uploadListConfig === "boolean" && uploadListConfig) {
      finalShowUploadListDragger = {
        showPreviewIcon: true,
        showRemoveIcon: true,
        showDownloadIcon: false,
      };
    }

    const draggerProps = {
      ...uploadProps,
      showUploadList: finalShowUploadListDragger,
      onPreview: handlePreview,
      isImageUrl: isImageUrl,
    };
    //@ts-ignore
    return React.createElement(Upload.Dragger, draggerProps);
  } else {
    return <ErrorWidget />;
  }
});
/**
 * Select,
 * Select.Options通过配置项options传人进去
 */
const WidgetSelect = React.memo((props: any) => {
  const { disabled, value, options, placeholder, ...restProps } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  const displayValue = disabled && !value ? "-" : value;

  // 在非禁用模式下，即使没有值也要显示 placeholder
  const selectPlaceholder = disabled && !value ? undefined : placeholder;

  return (
    <Select
      {...restProps}
      disabled={disabled}
      value={displayValue}
      options={options}
      placeholder={selectPlaceholder}
    />
  );
});
/**
 * AutoComplete 自动完成组件
 * 支持自定义输入和下拉选择
 */
const WidgetAutoComplete = React.memo((props: any) => {
  const {
    onSearch,
    options,
    loading,
    onSelect,
    disabled,
    value,
    ...restProps
  } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  const displayValue = disabled && !value ? "-" : value;

  const handleSearch = (searchValue: string) => {
    if (onSearch && typeof onSearch === "function") {
      onSearch(searchValue);
    }
  };

  const handleSelect = (selectValue: string, option: any) => {
    if (onSelect && typeof onSelect === "function") {
      onSelect(selectValue, option);
    }
  };

  // 只传递 AutoComplete 支持的有效属性
  // 移除可能导致警告的非标准属性
  const autoCompleteProps = {
    ...restProps,
    value: displayValue,
    options: options || [],
    onSearch: handleSearch,
    onSelect: handleSelect,
    notFoundContent: loading ? "加载中..." : "无匹配数据",
    disabled,
  };

  return <AutoComplete {...autoCompleteProps} />;
});
/**
 * RichTextEditor 富文本编辑器
 */
const WidgetRichTextEditor = React.memo((props: any) => {
  return <RichTextEditor {...props} />;
});

/**
 * InputNumber 数字输入框
 */
const WidgetInputNumber = React.memo((props: any) => {
  const { disabled, value, ...restProps } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  const displayValue =
    disabled && (value === null || value === undefined || value === "")
      ? undefined
      : value;
  const placeholder =
    disabled && (value === null || value === undefined || value === "")
      ? "-"
      : undefined;

  return (
    <InputNumber
      {...restProps}
      disabled={disabled}
      value={displayValue}
      placeholder={placeholder}
    />
  );
});

/**
 * Cascader 级联选择器
 * 支持多级联动选择
 */
const WidgetCascader = React.memo((props: any) => {
  // Cascader 不支持 loading 属性，需要过滤掉
  const {
    loading,
    placeholder: configPlaceholder,
    disabled,
    value,
    options,
    ...cascaderProps
  } = props;

  // 在只读/禁用模式下，如果值为空则显示 "-"
  const displayValue =
    disabled && (!value || (Array.isArray(value) && value.length === 0))
      ? null
      : value;
  const placeholder =
    disabled && (!value || (Array.isArray(value) && value.length === 0))
      ? "-"
      : configPlaceholder;

  return (
    <Cascader
      {...cascaderProps}
      disabled={disabled}
      value={displayValue}
      options={options}
      placeholder={placeholder}
    />
  );
});

/**
 * 对字符串widgetType进行转化为antd组件
 * 类似Input.Search这些组件进行特殊处理
 * @param widget
 */
const widgetsTran = ({
  widgetType,
  isNewRecord,
  isViewMode,
  ...widget
}: WidgetType & { isNewRecord?: boolean; isViewMode?: boolean }) => {
  try {
    if (widgetType === "RichTextEditor") {
      return <WidgetRichTextEditor {...widget} />;
    } else if (widgetType === "SupplierSelectInput") {
      return <SupplierSelectInput {...widget} />;
    } else if (widgetType === "AutoComplete") {
      return <WidgetAutoComplete {...widget} />;
    } else if (widgetType === "Cascader") {
      return <WidgetCascader {...widget} />;
    } else if (widgetType === "InputNumber") {
      //@ts-ignore
      return <WidgetInputNumber {...widget} />;
    } else if (widgetType.startsWith("Input")) {
      // Input开头的组件，包括Input.Search、Input.TextArea
      return <WidgetInput widgetType={widgetType} {...widget} />;
    } else if (widgetType.startsWith("DatePicker")) {
      return <WidgetDatePicker widgetType={widgetType} {...widget} />;
    } else if (widgetType.startsWith("Select")) {
      return <WidgetSelect {...widget} />;
    } else if (widgetType.startsWith("Checkbox")) {
      return <WidgetCheckbox widgetType={widgetType} {...widget} />;
    } else if (widgetType.startsWith("Radio")) {
      return <WidgetRadio widgetType={widgetType} {...widget} />;
    } else if (widgetType.startsWith("Upload")) {
      return <WidgetUpload widgetType={widgetType} {...widget} />;
    } else if (widgetType.startsWith("Button")) {
      return <WidgetButton {...widget} />;
    } else {
      return <ErrorWidget />;
    }
  } catch (e) {
    console.warn(e);
    return <ErrorWidget message={JSON.stringify(e)} />;
  }
};
export default widgetsTran;
